import {
  Dialog,
  PageLayout,
  type DialogProps,
  type PageLayoutContentProps,
  type PageLayoutFooterProps,
  type PageLayoutHeaderProps,
  type PageLayoutProps,
} from '@primer/react'
import {clsx} from 'clsx'
import {
  Children,
  isValidElement,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react'

import {
  CollapsibleSidebar,
  CollapsibleSidebarContext,
  type CollapsibleSidebarProps,
} from '../CollapsibleSidebar/CollapsibleSidebar'
import {useCollapsibleSidebarState} from '../CollapsibleSidebar/use-collapsible-sidebar-state'
import {useFooterBorder} from '../use-footer-border'
import {CollapsiblePageHeader} from './CollapsiblePageHeader'
import styles from './SidebarPageLayout.module.css'
import {
  SidebarPageLayoutActionsContext,
  SidebarPageLayoutStateContext,
  type SidebarPageLayoutActions,
  type SidebarPageLayoutState,
} from './state'
import {LayoutTopOffsetTracker} from './LayoutTopOffsetTracker'
import {SidebarTrigger, type SidebarPageLayoutSidebarTriggerProps} from './SidebarTrigger'
import {BannerStackOutlet, BannerStackProvider, type BannerStackProviderProps} from '@github-ui/banner-stack'
import {isFeatureEnabled} from '@github-ui/feature-flags'

/**
 * Returns true when the calling component is rendered inside a
 * `SidebarPageLayout` (i.e. the actions context is provided). Returns false
 * when no layout is mounted and the default `null` actions context is being
 * used. Useful for shared components rendered both inside and outside the
 * layout that need to know whether layout-dependent primitives (e.g.
 * `SidebarTrigger`) will actually function.
 */
export function useIsInsideSidebarPageLayout(): boolean {
  return use(SidebarPageLayoutActionsContext) !== null
}

/**
 * Public handle for imperatively controlling the enclosing
 * `SidebarPageLayout`: collapse/expand the desktop rail and open/close the
 * narrow-viewport dialog.
 */
export interface SidebarPageLayoutActionsHandle {
  /** Collapse the desktop sidebar rail to its condensed state. */
  collapse: () => void
  /** Expand the desktop sidebar rail. */
  expand: () => void
  /** Toggle the desktop sidebar rail between expanded and condensed. */
  toggleCollapse: () => void
  /** Open the narrow-viewport sidebar dialog. */
  openDialog: () => void
  /** Close the narrow-viewport sidebar dialog. */
  closeDialog: () => void
}

/**
 * Imperatively control the enclosing `SidebarPageLayout` from any component
 * rendered inside it (e.g. within the `Sidebar` or `Content` slots).
 * A shared component that renders both inside and outside a layout must not
 * call this hook conditionally (Rules of Hooks). Branch on
 * {@link useIsInsideSidebarPageLayout} to render a child that calls it
 * unconditionally.
 */
export function useSidebarPageLayoutActions(): SidebarPageLayoutActionsHandle {
  const actions = use(SidebarPageLayoutActionsContext)
  const publicActions = useMemo<SidebarPageLayoutActionsHandle | null>(() => {
    if (!actions) return null
    const {collapse, expand, toggleCollapse, openDialog, closeDialog} = actions
    return {collapse, expand, toggleCollapse, openDialog, closeDialog}
  }, [actions])
  if (!publicActions) {
    throw new Error('useSidebarPageLayoutActions must be called within a <SidebarPageLayout>.')
  }
  return publicActions
}

// ---------------------------------------------------------------------------
// Root

export type SidebarPageLayoutProps = Pick<PageLayoutProps, 'className' | 'style'> & {
  /** Props passed to the banner stack provider. */
  bannerProviderProps?: Omit<BannerStackProviderProps, 'children'>
  /**
   * When provided, the sidebar's expanded/collapsed state is persisted under
   * this logical key. Use the same key across pages within a navigation flow
   * (e.g. issues + milestones + labels) to keep the collapse state in sync.
   * The persistence hook handles both legacy standalone cookies and the shared
   * sidebar cookie format. When omitted, state is held in local component state
   * and resets on navigation.
   */
  storageKey?: string
  /**
   * Initial expanded state. Defaults to `true` (sidebar starts expanded).
   * Only used when there is no persisted value (or no `storageKey`).
   */
  defaultExpanded?: boolean
  /**
   * Server-read seed for the initial expanded value (prevents a flash on hard
   * navigation). Seeds the first render whether or not `storageKey` is set;
   * falls back to `defaultExpanded`.
   */
  initialExpanded?: boolean

  /**
   * When true, renders a bottom divider at the top of the footer.
   */
  showFooterBorder?: boolean
  /**
   * Opt in to sizing the layout to the *usable* viewport — the space below any
   * chrome above it (global header, staffbar, banners, etc.). The layout
   * measures its own distance from the top of the viewport and sets its
   * `min-height` to the remaining space, instead of a naive `100vh` that would
   * overflow by the height of that chrome. The measurement is imperative (no
   * re-render) and re-read on scroll/resize — see {@link LayoutTopOffsetTracker}.
   *
   * This controls the layout *height* only. Content is packed to the top of the
   * sized wrapper (`align-content: flex-start`) unless you also pass
   * {@link fillHeight} to stretch it.
   *
   * Opt-in: layouts that omit `fitViewport` attach no observers and behave
   * exactly as before (content sized to its natural height, sticky sidebar
   * preserved).
   */
  fitViewport?: boolean
  /**
   * Stretch content to fill the wrapper's height (`align-content: normal`)
   * instead of packing it to the top — for surfaces designed to fill the
   * container, like a chat pane (e.g. Copilot / agents).
   *
   * This controls *alignment* only, independently of {@link fitViewport}. On
   * its own it has nothing to stretch into unless the wrapper is taller than its
   * content, so it's typically paired with `fitViewport` (which sizes the
   * wrapper to the usable viewport first). Pairing the two gives a full-height,
   * content-filling layout; using `fitViewport` alone keeps the same height but
   * aligns content to the top.
   *
   * Only supported for layouts **without** a `Header` or `Footer`. The wrapper
   * is a wrap flex container whose header/content/footer are separate full-width
   * flex lines, so the stretch would distribute spare height across *every* line
   * — a header/footer would grow into large blank areas instead of only the
   * content filling the space. A development-only warning fires if `fillHeight`
   * is combined with a header or footer.
   */
  fillHeight?: boolean
}

function Root({
  children,
  bannerProviderProps,
  storageKey,
  defaultExpanded = true,
  initialExpanded,
  showFooterBorder = true,
  fitViewport,
  fillHeight,
  className,
  ...props
}: PropsWithChildren<SidebarPageLayoutProps>) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [triggerElement, setTriggerElement] = useState<HTMLButtonElement | null>(null)
  const [sidebarLabel, setSidebarLabel] = useState<string | undefined>(undefined)
  const [hasSidebar, setHasSidebar] = useState(false)

  useFooterBorder(showFooterBorder)
  // A `fitViewport` layout measures the offset from its own on-screen position
  // so it fits below whatever chrome sits above it (global header, banners,
  // etc.). The measurement lives in <LayoutTopOffsetTracker>, rendered below
  // only when `fitViewport` is set — so layouts that don't fit the viewport
  // mount no tracker, attach no observers/listeners, and the CSS falls back to
  // its `0px` default (and isn't consumed).
  const rootRef = useRef<HTMLElement | null>(null)

  // `fillHeight` stretches every flex line of the PageLayout wrapper, so a
  // Header/Footer would grow into blank space instead of only the content
  // filling the viewport. Warn (dev only) so the misuse is caught early.
  if (process.env.NODE_ENV !== 'production' && fillHeight) {
    // eslint-disable-next-line @eslint-react/no-children-to-array -- Children.toArray is safe here because it's a dev-only check.
    const hasHeaderOrFooter = Children.toArray(children).some(
      child => isValidElement(child) && (child.type === Header || child.type === Footer),
    )
    if (hasHeaderOrFooter) {
      // eslint-disable-next-line no-console
      console.warn(
        '[SidebarPageLayout] `fillHeight` is only supported for layouts without a Header or Footer. ' +
          'With a header/footer present, the full-height stretch distributes spare height across all ' +
          'regions instead of only the content. Remove `fillHeight`, or drop the Header/Footer.',
      )
    }
  }

  // Choose between persisted (cookie) and ephemeral (local) state.
  // useCollapsibleSidebarState must be called unconditionally; we pass a
  // sentinel key when the consumer didn't opt in and ignore its value.
  const persisted = useCollapsibleSidebarState({
    storageKey: storageKey ?? '__sidebar_page_layout_unused__',
    defaultExpanded,
    initialExpanded,
  })
  const [localExpanded, setLocalExpanded] = useState(initialExpanded ?? defaultExpanded)
  const isExpanded = storageKey ? persisted.isExpanded : localExpanded
  // Destructure the stable setter so the useCallback dep isn't the entire
  // (newly-allocated each render) `persisted` object.
  const persistedSetExpanded = persisted.setExpanded
  const setExpanded = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      // Pass functional updates through to the underlying setter so it can
      // resolve them against the latest state — evaluating against the
      // closure value here would lose updates on rapid successive calls.
      if (storageKey) {
        persistedSetExpanded(next)
      } else {
        setLocalExpanded(next)
      }
    },
    [storageKey, persistedSetExpanded],
  )

  const isCollapsed = !isExpanded

  const state = useMemo<SidebarPageLayoutState>(
    () => ({isDialogOpen, isCollapsed, triggerElement, sidebarLabel, hasSidebar}),
    [isDialogOpen, isCollapsed, triggerElement, sidebarLabel, hasSidebar],
  )

  const actions = useMemo<SidebarPageLayoutActions>(
    () => ({
      openDialog: () => setIsDialogOpen(true),
      closeDialog: () => setIsDialogOpen(false),
      setTriggerElement,
      collapse: () => setExpanded(false),
      expand: () => setExpanded(true),
      toggleCollapse: () => setExpanded(prev => !prev),
      setSidebarLabel,
      setHasSidebar,
    }),
    [setExpanded],
  )
  const useFooterRedesign = isFeatureEnabled('primer_footer_redesign')

  return (
    <BannerStackProvider {...bannerProviderProps}>
      <SidebarPageLayoutStateContext value={state}>
        <SidebarPageLayoutActionsContext value={actions}>
          {/*
          Render as <section> to suppress the implicit "banner" role on the
          inner <header> from PageLayout.Header. Without sectioning content,
          pages pairing this layout with the global nav banner would trigger
          a landmark-no-duplicate-banner axe violation.

          A <section> with no accessible name is not itself a landmark, so
          this does not introduce any new exposed regions to AT.
        */}
          <section
            ref={rootRef}
            className={styles.Root}
            data-sidebar-collapsed={isCollapsed || undefined}
            data-fit-viewport={fitViewport || undefined}
            data-fill-height={fillHeight || undefined}
          >
            {fitViewport ? <LayoutTopOffsetTracker rootRef={rootRef} /> : null}
            <PageLayout
              containerWidth="full"
              padding="none"
              columnGap="none"
              rowGap="none"
              _slotsConfig={{header: Header, footer: Footer, sidebar: Sidebar}}
              {...props}
              className={clsx(styles.PageLayoutRoot, useFooterRedesign && styles.FooterRedesignOffset, className)}
            >
              {children}
            </PageLayout>
          </section>
        </SidebarPageLayoutActionsContext>
      </SidebarPageLayoutStateContext>
    </BannerStackProvider>
  )
}

Root.displayName = 'SidebarPageLayout'

// ---------------------------------------------------------------------------
// Header
//
// Renders into PageLayout's `header` slot and owns the horizontal inset that
// keeps the header content aligned with `SidebarPageLayout.Content` below.
//
// The narrow-viewport sidebar trigger lives in the page header — use
// {@link CollapsiblePageHeader} (re-exported as `SidebarPageLayout.PageHeader`)
// to get a `PageHeader` with the trigger pre-wired. Drop down to the raw
// `<PageHeader>` from `@primer/react` if you need full control over the
// `LeadingAction` slot.

export type SidebarPageLayoutHeaderProps = Omit<PageLayoutHeaderProps, 'padding'> & {
  /**
   * Padding around the header. Defaults to `'normal'`. Match the padding on
   * `SidebarPageLayout.Content` when their inner edges should align.
   */
  padding?: PageLayoutHeaderProps['padding']
  /**
   * Maximum content width inside the header. Mirrors the `width` prop on
   * `SidebarPageLayout.Content` so the header title aligns with the content
   * column below it. Defaults to `'xlarge'` to match `Content`'s default.
   */
  width?: 'full' | 'medium' | 'large' | 'xlarge'
}

function Header({
  divider = 'none',
  padding = 'normal',
  width = 'xlarge',
  children,
  ...props
}: PropsWithChildren<SidebarPageLayoutHeaderProps>) {
  const focusFallbackRef = useRef<HTMLDivElement | null>(null)
  return (
    // PageLayout.Header owns the block padding, while `.HeaderInner` mirrors
    // the selected padding's horizontal inset so centered, width-constrained
    // header content follows the same spacing scale as PageLayout.Content.
    // eslint-disable-next-line primer-react/direct-slot-children -- SidebarPageLayout owns the slot config
    <PageLayout.Header padding={padding} divider={divider} className={styles.Header} {...props}>
      <div
        ref={focusFallbackRef}
        tabIndex={-1}
        className={styles.HeaderInner}
        data-width={width}
        data-padding={padding}
      >
        <BannerStackOutlet focusFallbackRef={focusFallbackRef} className="mb-4" />
        {children}
      </div>
    </PageLayout.Header>
  )
}

Header.displayName = 'SidebarPageLayout.Header'

// ---------------------------------------------------------------------------
// Sidebar — renders content as a CollapsibleSidebar slot in PageLayout + Dialog when open
//
// Note: Unlike PageLayout.Sidebar which provides its own sticky positioning and
// width grid, we render CollapsibleSidebar directly so we get its built-in
// sticky footer + sentinel-based clipping detection + condensed mode + width
// transitions. PageLayout's slot system supports any element with the
// `__SLOT__: PageLayout.Sidebar.__SLOT__` marker — CollapsibleSidebar already
// has this set, and our Sidebar function is registered via _slotsConfig so
// whatever it returns becomes the sidebar slot content.

export type SidebarFooterRenderArgs = {
  /** Whether the sidebar is currently expanded (vs. condensed). */
  isExpanded: boolean
  /** Toggle the sidebar between expanded and condensed. */
  toggle: () => void
}

export type SidebarPageLayoutSidebarProps = {
  /** Title for the narrow-viewport Dialog. Defaults to aria-label or "Sidebar". */
  dialogTitle?: string
  /**
   * Content for the sidebar footer. When omitted, a default "Collapse
   * sidebar" button is rendered. Pass `null` to suppress the footer.
   *
   * May also be a render function that receives `{isExpanded, toggle}` so
   * that footer items (feedback links, additional toggles) can adapt to the
   * collapsed state without separately reading from context. This mirrors
   * the `renderFooter` API on `ThreePanesLayout`.
   */
  footer?: ReactNode | null | ((args: SidebarFooterRenderArgs) => ReactNode)
  /** Hide the default collapse footer. Only applies when footer is not explicitly provided. */
  hideCollapseButton?: boolean
  /** Accessible label for the sidebar. */
  'aria-label'?: string
  /**
   * Extra inline padding around the children content (in addition to the 8px
   * inset that `CollapsibleSidebar` already applies to its rail). Defaults
   * to `'none'`. Use `'condensed'` (8px) or `'normal'` (16px) to add more.
   * Does not affect the footer.
   */
  padding?: 'none' | 'condensed' | 'normal'
  /** Expanded sidebar width. Mirrors CollapsibleSidebar's expandedWidth. */
  expandedWidth?: 'small' | 'medium' | 'large'
  /** Add a resize handle to the expanded desktop sidebar rail. */
  resizable?: CollapsibleSidebarProps['resizable']
  /** Smallest width (px) the desktop sidebar can be resized to. */
  minWidth?: CollapsibleSidebarProps['minWidth']
  /** Largest width (px) the desktop sidebar can be resized to. */
  maxWidth?: CollapsibleSidebarProps['maxWidth']
  /** Cookie key used to persist the resized desktop sidebar width. */
  widthStorageKey?: CollapsibleSidebarProps['widthStorageKey']
  /** Server-read width seed (px) for the desktop sidebar. */
  initialWidth?: CollapsibleSidebarProps['initialWidth']
  /** Called with the committed width whenever a desktop resize operation ends. */
  onResizeEnd?: CollapsibleSidebarProps['onResizeEnd']
  /**
   * Width of the narrow-viewport Dialog that holds the sidebar contents.
   * Defaults to `'small'` (296px) since most sidebar contents are
   * navigation links and don't need horizontal room. Use `'medium'`
   * (320px), `'large'` (480px), or `'xlarge'` (640px) when the sidebar
   * holds wider content (e.g. complex filter forms).
   */
  dialogWidth?: 'small' | 'medium' | 'large' | 'xlarge'
  /**
   * Position of the sidebar Defaults to `'left'` so the
   * sidebar slides in from the side. Pass a Primer `ResponsiveValue` (e.g.
   * `{narrow: 'bottom', regular: 'left'}`) to render as a bottom sheet on
   * narrow viewports while keeping the left-anchored dialog for wider
   * breakpoints that still hit the dialog path.
   */
  dialogPosition?: DialogProps['position']
  /**
   * When `true`, the sidebar rail scrolls its own overflowing content
   * vertically (within its full height) instead of clipping it behind the
   * sticky footer. Defaults to `false`. Opt in for sidebars whose content can
   * grow taller than the viewport (e.g. a long, paginated list).
   */
  scrollable?: boolean
}

const DIALOG_CONTEXT_VALUE = {
  isExpanded: true,
  isExpansionSettled: true,
  visualIsExpanded: true,
  isInDialog: true,
} as const

function DefaultCollapseButton() {
  // isExpanded and onToggle are read from CollapsibleSidebarContext (which
  // our parent <CollapsibleSidebar> populates from SidebarPageLayoutActions).
  return <CollapsibleSidebar.Toggle />
}

function Sidebar({
  padding = 'none',
  dialogTitle,
  'aria-label': ariaLabel,
  footer,
  hideCollapseButton,
  expandedWidth = 'small',
  resizable = false,
  minWidth,
  maxWidth,
  widthStorageKey,
  initialWidth,
  onResizeEnd,
  dialogWidth = 'small',
  dialogPosition = 'left',
  scrollable = false,
  children,
}: PropsWithChildren<SidebarPageLayoutSidebarProps>) {
  const {isDialogOpen, isCollapsed, triggerElement} = use(SidebarPageLayoutStateContext)
  const actions = use(SidebarPageLayoutActionsContext)
  const title = dialogTitle ?? ariaLabel ?? 'Sidebar'
  // Build a stable ref-shaped object pointing at the latest registered trigger
  // element. Using useMemo (instead of a useRef + useEffect) guarantees the
  // Dialog sees the current trigger immediately on mount, even if the trigger
  // registers in the same render that opens the dialog.
  const returnFocusRef = useMemo(() => ({current: triggerElement}), [triggerElement])

  // Publish our aria-label so SidebarTrigger can derive a more semantic
  // accessible name (e.g. "Open Issue views" instead of "Open sidebar").
  const setSidebarLabel = actions?.setSidebarLabel
  const setHasSidebar = actions?.setHasSidebar
  useEffect(() => {
    setSidebarLabel?.(ariaLabel)
    return () => setSidebarLabel?.(undefined)
  }, [ariaLabel, setSidebarLabel])

  // Register that a Sidebar is mounted so CollapsiblePageHeader can gate
  // its auto-injected trigger (avoids a dead button when no sidebar exists).
  useEffect(() => {
    setHasSidebar?.(true)
    return () => setHasSidebar?.(false)
  }, [setHasSidebar])

  const toggle = actions?.toggleCollapse ?? (() => {})

  // Determine the effective footer content for desktop and dialog separately.
  // Render functions get an `isExpanded` argument matching the render context:
  //   - desktop sidebar: tracks the actual expanded/collapsed state
  //   - dialog: always `true` (the dialog has no condensed mode)
  // - `footer === null` or `hideCollapseButton`: no footer
  // - `typeof footer === 'function'`: invoke render prop
  // - `footer` truthy: use as ReactNode
  // - otherwise: default Collapse button (desktop only — see below)
  //
  // The Dialog branch never falls through to the default Collapse button:
  // `<CollapsibleSidebar.Toggle>` returns `null` when `isInDialog` is true
  // (there's no rail to collapse inside the dialog), so falling back to it
  // would render an empty Dialog footer container with no visible content.
  // Consumers who explicitly pass a footer (node or render prop) opt in to
  // having it appear in both contexts.
  const renderFooter = (forDialog: boolean): ReactNode => {
    if (footer === null || hideCollapseButton) return null
    if (typeof footer === 'function') {
      return footer({isExpanded: forDialog ? true : !isCollapsed, toggle: forDialog ? () => {} : toggle})
    }
    if (footer) return footer
    return forDialog ? null : <DefaultCollapseButton />
  }
  const sidebarFooter = renderFooter(false)
  const dialogFooter = renderFooter(true)

  const paddingValue =
    padding === 'normal' ? 'var(--base-size-16)' : padding === 'condensed' ? 'var(--base-size-8)' : '0'

  return (
    <>
      <CollapsibleSidebar
        isExpanded={!isCollapsed}
        inert={isDialogOpen || undefined}
        onToggle={toggle}
        ariaLabel={ariaLabel}
        expandedWidth={expandedWidth}
        resizable={resizable}
        minWidth={minWidth}
        maxWidth={maxWidth}
        widthStorageKey={widthStorageKey}
        initialWidth={initialWidth}
        onResizeEnd={onResizeEnd}
        footer={sidebarFooter}
        scrollable={scrollable}
        className={styles.Sidebar}
      >
        <div style={{padding: paddingValue}}>{children}</div>
      </CollapsibleSidebar>
      {isDialogOpen && actions ? (
        <Dialog
          title={title}
          onClose={actions.closeDialog}
          position={dialogPosition}
          width={dialogWidth}
          returnFocusRef={returnFocusRef}
          renderBody={({children: dialogChildren}) => (
            <Dialog.Body className={styles.DialogBody}>{dialogChildren}</Dialog.Body>
          )}
          renderFooter={
            dialogFooter
              ? () => (
                  <CollapsibleSidebarContext value={DIALOG_CONTEXT_VALUE}>
                    <div className={styles.DialogFooter}>{dialogFooter}</div>
                  </CollapsibleSidebarContext>
                )
              : undefined
          }
        >
          <CollapsibleSidebarContext value={DIALOG_CONTEXT_VALUE}>
            <div style={{padding: paddingValue}}>{children}</div>
          </CollapsibleSidebarContext>
        </Dialog>
      ) : null}
    </>
  )
}

Sidebar.displayName = 'SidebarPageLayout.Sidebar'
// Mark as the PageLayout sidebar slot so PageLayout positions it correctly.
;(Sidebar as unknown as {__SLOT__: symbol}).__SLOT__ = (PageLayout.Sidebar as unknown as {__SLOT__: symbol}).__SLOT__

// ---------------------------------------------------------------------------
// Content

export type SidebarPageLayoutContentProps = PageLayoutContentProps

function Content({width = 'xlarge', padding = 'normal', ...props}: PropsWithChildren<SidebarPageLayoutContentProps>) {
  return <PageLayout.Content width={width} padding={padding} {...props} />
}

Content.displayName = 'SidebarPageLayout.Content'

// ---------------------------------------------------------------------------
// Footer

export type SidebarPageLayoutFooterProps = PageLayoutFooterProps

function Footer({padding = 'normal', divider = 'line', ...props}: PropsWithChildren<SidebarPageLayoutFooterProps>) {
  return (
    // eslint-disable-next-line primer-react/direct-slot-children -- SidebarPageLayout owns the slot config
    <PageLayout.Footer padding={padding} divider={divider} {...props} />
  )
}

Footer.displayName = 'SidebarPageLayout.Footer'

// ---------------------------------------------------------------------------
// Compound export

export const SidebarPageLayout = Object.assign(Root, {
  Header,
  PageHeader: CollapsiblePageHeader,
  SidebarTrigger,
  Sidebar,
  Content,
  Footer,
})

// Re-export sub-components as named exports so react-docgen-typescript can
// extract their prop types when referenced via `<ArgTypes of={...} />` in
// MDX docs. Without these, accessing them only via `SidebarPageLayout.Header`
// hides them from the docgen plugin (which only walks top-level exports).
export {Root as SidebarPageLayoutRoot}
export {Header as SidebarPageLayoutHeader}
export const SidebarPageLayoutPageHeader = CollapsiblePageHeader
export const SidebarPageLayoutSidebarTrigger = SidebarTrigger
// Re-export so the docgen plugin (which only walks top-level exports) picks
// up the props type alongside the SidebarTrigger component.
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {SidebarPageLayoutSidebarTriggerProps}
export {Sidebar as SidebarPageLayoutSidebar}
export {Content as SidebarPageLayoutContent}
export {Footer as SidebarPageLayoutFooter}
